/**
 * Parses Godot resource references (`SubResource("id")`, `ExtResource("id")`) and
 * resolves them against the scene's resource tables. An R3F component resolves
 * geometry and material itself, through the parsers in `meshes/` and `materials/`.
 */
import { resourceRef } from '../godot/variant/resourceRef.js';
import { simplifyResPath } from '../godot/string/string.js';
import { GODOT_SHADER_EXTENSION, GODOT_TEXT_RESOURCE_FILE_EXTENSION, } from '../godot/project/resourceFormats.js';
import { resourceSliceRegistry } from './sliceRegistration.js';
import { fileExtension } from './fileExtension.js';
import { firstWith, lastWith } from '../utils/keyedLookup.js';
import { CANVAS_TEXTURE_TYPE } from './textures/canvastexture/types.js';
/**
 * The last `[ext_resource]` keeps an id: Godot's loader assigns `ext_resources[id]` per entry
 * (`resource_format_text.cpp:517-519`), and every reference follows the whole table.
 */
const lastExternalWithId = lastWith((resource) => resource.id);
/** The first entry in declaration order keeps an id, so a repeated id resolves to its first declaration. */
const firstInternalWithId = firstWith((resource) => resource.id);
/** The `[ext_resource]` declaring `id`, the last one when a file repeats an id. */
export function findExtResource(externalResources, id) {
    // An empty table answers nothing, and a caller's `?? []` is a fresh array per call.
    if (externalResources.length === 0)
        return undefined;
    return lastExternalWithId(externalResources, id);
}
export function parseResourceReference(ref) {
    const parsed = resourceRef(ref);
    return parsed ? { type: parsed.kind, id: parsed.id } : null;
}
/**
 * Resolve an `ExtResource("id")` reference, or a raw `res://` path that passes
 * through, to a `res://` path. Null when the reference matches neither form or
 * the id is not registered.
 */
export function resolveExtResourcePath(ref, externalResources) {
    if (!ref)
        return null;
    // Simplified on the way out, both arms: Godot runs every resource address
    // through `String::simplify_path`, and a real scene writes the redundant
    // slashes it collapses (`res:///addons/...`).
    if (ref.startsWith('res://'))
        return simplifyResPath(ref);
    const parsed = parseResourceReference(ref);
    if (!parsed || parsed.type !== 'ExtResource')
        return null;
    const path = findExtResource(externalResources, parsed.id)?.path;
    return path === undefined ? null : simplifyResPath(path);
}
/**
 * The file an external reference loads as a ParsedResource: a resource file or a `.gdshader`.
 * Null for any other reference, such as an image: no slice routes it to the `resource` bus, so a
 * request for one would wait forever.
 */
export function parsedResourcePath(ref, externalResources) {
    const path = resolveExtResourcePath(ref, externalResources);
    if (!path)
        return null;
    return isResourceFilePath(path) || fileExtension(path) === GODOT_SHADER_EXTENSION ? path : null;
}
/**
 * Whether `path` names a resource file, which records its own type: a `.tres`, or a binary file a
 * slice routes to the `resource` bus, such as a `.res`.
 */
export function isResourceFilePath(path) {
    return (fileExtension(path) === GODOT_TEXT_RESOURCE_FILE_EXTENSION ||
        resourceSliceRegistry.busTypeOfPath(path) === 'resource');
}
/** Find a SubResource by id. The first resource in declaration order with that id wins. */
export function findSubResource(internalResources, id) {
    if (internalResources.length === 0)
        return undefined;
    return firstInternalWithId(internalResources, id);
}
/**
 * The first resource of `type` declared under `id`, in declaration order. The id map
 * answers when that id's first holder has the type. A file that repeats the id with
 * another type first falls back to the scan. An id the map lacks has no holder at all.
 */
export function findSubResourceOfType(internalResources, id, type) {
    const firstHolder = findSubResource(internalResources, id);
    if (!firstHolder)
        return undefined;
    if (firstHolder.id === id && firstHolder.type === type)
        return firstHolder;
    return internalResources.find((r) => r.id === id && r.type === type);
}
/**
 * Resolve a raw `SubResource("id")` property string to the internal resource it
 * names. Undefined for an absent value, a reference of another form, or an unknown id.
 */
export function resolveSubResourceRef(ref, internalResources) {
    if (!ref)
        return undefined;
    const parsed = parseResourceReference(ref);
    if (!parsed || parsed.type !== 'SubResource')
        return undefined;
    return findSubResource(internalResources, parsed.id);
}
/**
 * Peel `CanvasTexture` wrappers off a Texture2D reference, to fixed point, down to
 * the inner `diffuse_texture` reference Godot draws. The reference, not a path, lets
 * it compose with an image or an inline `GradientTexture2D`. A wrapper with no
 * `diffuse_texture`, or a cycle, returns undefined: neither names anything to draw.
 */
export function unwrapCanvasTextureRef(ref, internalResources) {
    return peelCanvasTextures(ref, internalResources).ref;
}
const NO_WRAPPERS = [];
/** `unwrapCanvasTextureRef`, with the wrappers it peels, for a caller that reads both. */
export function peelCanvasTextures(ref, internalResources) {
    let current = ref;
    // Fixed point, not a fixed count: a painter and a layout solve that peel N levels
    // agree only up to the shallower chain. Applying it twice changes nothing. `wrappers`
    // is allocated only on the first wrapper, since most references are not one.
    let wrappers;
    while (current) {
        const sub = resolveSubResourceRef(current, internalResources);
        if (sub?.type !== CANVAS_TEXTURE_TYPE)
            return { ref: current, wrappers: wrappers ?? NO_WRAPPERS };
        if (wrappers?.includes(sub))
            return { ref: undefined, wrappers };
        (wrappers ??= []).push(sub);
        current = sub.data.diffuse_texture;
    }
    return { ref: undefined, wrappers: wrappers ?? NO_WRAPPERS };
}
/**
 * The resource file an `ExtResource` reference names, which the caller parses on the
 * `resource` bus to learn what it holds. Null for any other form, a raw `res://` path included,
 * which `parsedResourcePath` accepts.
 */
export function resolveExtResourceFilePath(ref, externalResources) {
    const parsed = parseResourceReference(ref ?? '');
    if (!parsed || parsed.type !== 'ExtResource')
        return null;
    const resource = findExtResource(externalResources, parsed.id);
    if (!resource)
        return null;
    // The `[ext_resource]` `type=` cannot gate this: Godot writes it from the property
    // slot, so an AtlasTexture in a `texture` slot is recorded `type="Texture2D"`.
    // It lives here, not in the atlastexture slice, which imports this module.
    return isResourceFilePath(resource.path) ? resource.path : null;
}
/**
 * Resolve a **Texture2D-valued** property (`res://path`, `ExtResource("id")`, or a
 * `SubResource("id")` CanvasTexture) to the path of a file to load. A node component
 * asks `useTexture2D` instead: this returns null for a texture with no file, such as
 * an inline `GradientTexture2D`.
 */
export function resolveTexture2DPath(ref, externalResources, internalResources) {
    const unwrapped = unwrapCanvasTextureRef(ref, internalResources);
    // An AtlasTexture, inline or in a resource file, resolves to null, not its sheet: this answer
    // also sizes a Control's slot, and an atlas is the size of its region.
    // `inlineTexture2DSize` and `extResourceTexture2DSize` answer that size.
    if (resolveExtResourceFilePath(unwrapped, externalResources))
        return null;
    return resolveExtResourcePath(unwrapped, externalResources);
}
/** Resolve a Node's `instance` PackedScene reference to a `res://` scene path. */
export function resolveInstancePath(instanceRef, externalResources) {
    return resolveExtResourcePath(instanceRef, externalResources);
}
//# sourceMappingURL=SubResourceResolver.js.map